import { Box, Select, Switch, Text } from '@rsos/base-components';
import type { SelectOption } from '@rsos/base-components/Select';
import styled from '@rsos/theme/styled';
import type { FlagType } from '@rsos/utils/openFeature';

const Item = styled(Box).attrs({
  horizontal: true,
  justify: 'space-between',
  align: 'center',
  padding: '4px 8px',
})`
&:hover {
  background-color: ${({ theme }) => theme.colors.highlights};
`;

const SelectWrapper = styled(Box)`
  width: 180px;
`;

// Keep option/selected labels short so long strings — and JSON for object flags —
// don't blow out the control. The full value is still what's applied.
const truncate = (text: string) =>
  text.length > 28 ? `${text.slice(0, 28)}…` : text;

const displayValue = (value: unknown) =>
  typeof value === 'object' ? JSON.stringify(value) : String(value);

interface V2FlagOverrideItemProps {
  flagKey: string;
  type: FlagType;
  /** Effective value — the local override if set, otherwise the endpoint value. */
  value: unknown;
  overridden: boolean;
  /**
   * Registry-declared description, shown on hover of the flag key. Datadog doesn't
   * send descriptions to the client, so a flag not in the registry has none.
   */
  description?: string;
  /**
   * Registry-declared override options for a non-boolean flag. Absent (or a single
   * option) renders a disabled select of the current value — nothing to pick.
   */
  options?: readonly (string | number | object)[];
  /** Toggle a boolean flag's local override. */
  onToggle: (flagKey: string, next: boolean) => void;
  /** Set a non-boolean flag's local override to a chosen option. */
  onSelect: (flagKey: string, value: unknown) => void;
  /** Clear this flag's local override (reverts to the endpoint value). */
  onClear: (flagKey: string) => void;
}

const V2FlagOverrideItem = ({
  flagKey,
  type,
  value,
  overridden,
  description,
  options,
  onToggle,
  onSelect,
  onClear,
}: V2FlagOverrideItemProps) => {
  const renderControl = () => {
    if (type === 'boolean') {
      return (
        <Switch
          isOn={Boolean(value)}
          // debug-panel gates the panel itself, so overriding it would hide the controls.
          disabled={flagKey === 'debug-panel'}
          onClick={() => onToggle(flagKey, !value)}
        />
      );
    }

    // Non-boolean flags are always a select. Options come from the registry; a flag
    // with none (the endpoint returns only the assigned variation, not the full
    // set) falls back to its current value as a single, disabled option.
    const values = options?.length ? options : [value];
    const selectOptions: SelectOption[] = values.map(option => ({
      value: option,
      // Full value in the label; formatOptionLabel truncates the visible text and
      // exposes the full thing on hover (native title, for multi-key JSON etc.).
      label: displayValue(option),
    }));
    const selected =
      selectOptions.find(
        option => displayValue(option.value) === displayValue(value),
      ) ?? null;
    // Nothing to pick from a single/absent option → inert and grayed. (A leftover
    // override on such a flag is auto-cleared by the panel, so it won't sit here
    // stuck as overridden.)
    const disabled = selectOptions.length <= 1;

    return (
      // Disabled react-select swallows pointer events, so hang the full-value title
      // on the wrapper (still gets hover) — otherwise disabled JSON rows have no tooltip.
      <SelectWrapper
        title={disabled ? displayValue(value) : undefined}
        style={disabled ? { opacity: 0.6 } : undefined}
      >
        <Select
          options={selectOptions}
          value={selected}
          isClearable={overridden}
          isDisabled={disabled}
          isSearchable={false}
          // Truncate the visible label; full value on hover (native title).
          formatOptionLabel={(option: SelectOption) => (
            <span title={String(option.label)}>
              {truncate(String(option.label))}
            </span>
          )}
          onChange={(option: SelectOption | null) =>
            option ? onSelect(flagKey, option.value) : onClear(flagKey)
          }
        />
      </SelectWrapper>
    );
  };

  return (
    <Item>
      <Box>
        <Text size="normal" color="primaryText">
          <span title={description}>{flagKey}</span>
          {overridden ? ' •' : ''}
        </Text>
        <Text size="small" color="secondaryText">
          {type}
        </Text>
      </Box>
      {renderControl()}
    </Item>
  );
};

export default V2FlagOverrideItem;
